import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import SiteSlider from 'Components/Slider/SiteSlider';
import ProductCover from 'Components/Media/Image/ProductCover';
import { makeSliderSettings } from 'Components/Card/HitCardPopup/HitCardPopupScreenshotCarousel';
import { buildBundleGridSlides } from 'Components/Card/HitCardPopup/HitCardPopupBundleGridLayout';

import 'Components/Card/HitCardPopup/HitCardPopupScreenshotCarousel.scss';
import 'Components/Card/HitCardPopup/HitCardPopupBundleGrid.scss';

const BundleGridTile = ({ tile, sizes }) => {
  if (tile.type === 'cover') {
    const { cover, name, type } = tile.cover;

    return (
      <div className="HitCardPopupBundleGrid__tile">
        <ProductCover
          cover={cover}
          alt={`Product cover for ${name}`}
          displayType={type}
          sizesOverride={sizes}
        />
      </div>
    );
  }

  if (tile.type === 'more') {
    return (
      <div className="HitCardPopupBundleGrid__tile HitCardPopupBundleGrid__tile--more">
        <span className="HitCardPopupBundleGrid__tile__more">
          <FormattedMessage id="hitcardpopup.bundle-grid.more" defaultMessage="+ More" />
        </span>
      </div>
    );
  }

  return <div className="HitCardPopupBundleGrid__tile HitCardPopupBundleGrid__tile--blank" />;
};

BundleGridTile.propTypes = {
  tile: PropTypes.shape({
    type: PropTypes.oneOf(['cover', 'blank', 'more']).isRequired,
    cover: PropTypes.shape({
      cover: PropTypes.string,
      name: PropTypes.string,
      type: PropTypes.string,
    }),
  }).isRequired,
  sizes: PropTypes.string.isRequired,
};

/**
 * Replaces the screenshot carousel in the hit card popup for bundles (606PopupHitCardBundleCoverGrid).
 * Shows the bundle's packshots four to a slide; which packshots go where is decided by
 * buildBundleGridSlides in HitCardPopupBundleGridLayout.
 */
const HitCardPopupBundleGrid = ({ bundleCovers, itemCount, popupWidth, linkPath, linkFunc }) => {
  const slides = buildBundleGridSlides(bundleCovers, itemCount);
  if (slides.length === 0) return null;

  // Each tile is half the popup's width, unless it's a single cover shown full size
  const tileSizes = `${Math.round(popupWidth / 2)}px`;
  const singleTileSizes = `${popupWidth}px`;

  return (
    <div className="HitcardPopupScreenshotCarousel HitCardPopupBundleGrid">
      <SiteSlider
        fullWidth
        sliderSettings={makeSliderSettings(popupWidth)}
        sliderClass="HitcardPopupScreenshotCarousel__slider"
      >
        {slides.map((tiles, slideIndex) => {
          const isSingle = tiles.length === 1;

          return (
            <Link
              to={linkPath}
              className={classNames('HitCardPopupBundleGrid__slide', { 'HitCardPopupBundleGrid__slide--single': isSingle })}
              // eslint-disable-next-line react/no-array-index-key
              key={`slide-${slideIndex}`}
              onClick={linkFunc}
            >
              {tiles.map((tile, tileIndex) => (
                // eslint-disable-next-line react/no-array-index-key
                <BundleGridTile tile={tile} sizes={isSingle ? singleTileSizes : tileSizes} key={`${tile.type}-${tileIndex}`} />
              ))}
            </Link>
          );
        })}
      </SiteSlider>
    </div>
  );
};

HitCardPopupBundleGrid.propTypes = {
  bundleCovers: PropTypes.arrayOf(PropTypes.shape({
    cover: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
  })),
  itemCount: PropTypes.number,
  popupWidth: PropTypes.number,
  linkPath: PropTypes.string,
  linkFunc: PropTypes.func,
};

HitCardPopupBundleGrid.defaultProps = {
  bundleCovers: [],
  itemCount: 0,
  popupWidth: 400,
  linkPath: '',
  linkFunc: () => { },
};

export default HitCardPopupBundleGrid;
